import { observer } from 'mobx-react-lite';
import { isConnectedAccount } from 'owa-accounts-store';
import { openItemButtonAriaLabel } from 'owa-locstrings/lib/strings/openitembuttonarialabel.locstring.json';
import loc from 'owa-localize';
import PeekContentSelector from './PeekContentSelector';
import { getCalendarEventColorScheme } from 'owa-calendar-color-resolver';
import { hasReadRights } from 'owa-calendar-event-capabilities';
import CalendarBar from 'owa-calendar-forms-common/lib/components/CalendarBar';
import { getFullCalendarEvent } from 'owa-calendar-forms-common/lib/helpers/eventStore';
import type { ClientItemId } from 'owa-client-ids';
import { ControlIcons } from 'owa-control-icons';
import type EventScope from 'owa-service/lib/contract/EventScope';
import type ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';
import TxpSource from 'owa-txp/lib/store/viewSchema/TxpSource';
import { areAnyTXPEntitiesSupported } from 'owa-txp-common';
import { viewInMail } from 'owa-txp/lib/utils/TxpUtils';
import * as React from 'react';
import { isFeatureEnabled } from 'owa-feature-flags';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import PeekEventCommandBar from './PeekEventCommandBar';
import type { ReadingPaneSection } from 'owa-calendar-helpers-types/lib/ReadingPaneSection';
import { closeItemPeek } from 'owa-calendar-peek';

import styles from './CalendarItemPeek.scss';
import classnamesBind from 'classnames/bind';
let classNames = classnamesBind.bind(styles);

export interface CalendarItemPeekProps {
    itemId: ClientItemId;
    onOpenEventClicked?: (
        ev?: React.MouseEvent<HTMLElement>,
        scrollToSection?: ReadingPaneSection
    ) => void;
    onEditClicked: (eventScope: EventScope) => void;
    onDeleteClicked: (eventScope: EventScope) => void;
    onRespondClicked: (
        newResponseType: ResponseTypeType,
        eventScope: EventScope,
        sendMessage: boolean,
        notes: string
    ) => void;
    onReviewProposedNewTimeClicked?: (event?: Partial<CalendarEvent>) => void;
}
/**
 * This is the actual peek content for a calendar item
 */
export default observer(function CalendarItemPeek(props: CalendarItemPeekProps) {
    const {
        itemId,
        onOpenEventClicked,
        onDeleteClicked,
        onRespondClicked,
        onReviewProposedNewTimeClicked,
    } = props;
    const event = getFullCalendarEvent(null, props.itemId.Id);

    const onMouseDown = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        // prevent mouseDown event from propagating outside the peek content so it's not interpreted as a drag
        e.stopPropagation();
    }, []);
    const onViewInMailClicked = React.useCallback(() => {
        viewInMail(event);
    }, [event]);
    if (!event) {
        // If the item is not available then we don't render anything
        return null;
    }

    const calendarColorScheme = getCalendarEventColorScheme(event);
    const isTxp = areAnyTXPEntitiesSupported(event.EntityNamesMap);
    // Open button only available if item can be read.
    const buttonProps = hasReadRights(event)
        ? [
              {
                  onClick: onOpenEventClicked as () => void,
                  iconProps: {
                      iconName: ControlIcons.FullScreen,
                      styles: {
                          root: {
                              fontSize: '15px',
                              color: calendarColorScheme.accessibleText.calPrimaryBackground,
                          },
                      },
                  },
                  title: loc(openItemButtonAriaLabel),
                  ariaLabel: loc(openItemButtonAriaLabel),
              },
          ]
        : null;
    const hideViewInMailButton = isConnectedAccount(event.ParentFolderId.mailboxInfo?.userIdentity);
    return (
        // tabIndex = -1 ensures that the peek takes focus on click (even on blank area) but doesn't impact the tab order
        <div
            className={classNames(styles.peekContainer, styles.flexContainer, {
                [styles.txpWiderPeekContainer]: isTxp,
            })}
            tabIndex={-1}
            key={itemId.Id}>
            <CalendarBar
                calendarColorScheme={calendarColorScheme}
                buttonProps={buttonProps}
                className={
                    isFeatureEnabled('cal-surface-dragPeek') ? styles.calendarItemBar : undefined
                }
            />
            <div className={styles.content} onMouseDown={onMouseDown}>
                <div className={styles.peekScrollSection}>
                    <PeekContentSelector
                        event={event}
                        colorScheme={calendarColorScheme}
                        txpSource={TxpSource.CalendarPeek}
                        onViewInMailClicked={!hideViewInMailButton && onViewInMailClicked}
                        viewType={'SurfacePeek'}
                        onEditClicked={props.onEditClicked}
                        onDeleteClicked={onDeleteClicked}
                        onRespondClicked={onRespondClicked}
                        onReviewProposedNewTimeClicked={onReviewProposedNewTimeClicked}
                        onOpenEventClicked={onOpenEventClicked}
                        allowFullBody={false}
                        showInlineCommandBar={false}
                        onDismiss={closeItemPeek}
                    />
                </div>
                {!isTxp && (
                    <PeekEventCommandBar
                        key={event.ItemId.Id}
                        item={event}
                        colorScheme={calendarColorScheme}
                        actionSource={'SurfacePeek'}
                        onEditClicked={props.onEditClicked}
                        onDeleteClicked={onDeleteClicked}
                        onRespondClicked={onRespondClicked}
                        showInlineCommandBar={false}
                        onDismiss={closeItemPeek}
                    />
                )}
            </div>
        </div>
    );
});
